import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const Auth = ({ onLogin }) => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    userMode: 'Agent',
    // Agent-specific fields
    agencyName: '',
    registrationNumber: '',
    agencyAddress: '',
    city: '',
    postalCode: '',
    identityNumber: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!isLogin && !formData.fullName) {
      toast.error('Please enter your full name');
      return;
    }

    try {
      setLoading(true);
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      // Store token and user info
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('user_id', response.data.user.id);
      localStorage.setItem('user_email', response.data.user.email);
      localStorage.setItem('user_name', response.data.user.fullName);
      localStorage.setItem('app_user_mode', response.data.user.userMode);

      toast.success(isLogin ? 'Login successful!' : 'Account created successfully!');
      
      // Call onLogin callback
      onLogin(response.data.user);
      
      // Navigate based on user mode
      if (response.data.user.userMode === 'Influencer') {
        navigate('/influencer-portal');
      } else if (response.data.user.userMode === 'Contractor') {
        navigate('/contractor/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Auth error:', error);
      const errorMsg = error.response?.data?.detail || (isLogin ? 'Login failed' : 'Registration failed');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('If an account exists with this email, you will receive a password reset link.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error('Failed to process request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 px-4 py-8">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
              <i className="fas fa-key text-emerald-600 mr-2"></i>
              Reset Password
            </CardTitle>
            <p className="text-center text-gray-600">
              Enter your email and we'll send you a reset link
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <Label htmlFor="forgotEmail">Email Address *</Label>
                <Input
                  id="forgotEmail"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="john@example.com"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-emerald-600 hover:bg-emerald-700" 
                disabled={loading}
                data-testid="send-reset-link-btn"
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane mr-2"></i>
                    Send Reset Link
                  </>
                )}
              </Button>
              
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotEmail('');
                  }}
                  className="text-emerald-600 hover:underline text-sm"
                >
                  <i className="fas fa-arrow-left mr-1"></i>
                  Back to Login
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-3xl font-bold text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </CardTitle>
          <p className="text-center text-gray-600">
            {isLogin ? 'Login to your Flippy Homes account' : 'Join Flippy Homes today'}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input
                    id="fullName"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    placeholder="John Doe"
                    required={!isLogin}
                    data-testid="fullname-input"
                  />
                </div>

                <div>
                  <Label htmlFor="userMode">I am a *</Label>
                  <Select 
                    value={formData.userMode} 
                    onValueChange={(value) => setFormData({ ...formData, userMode: value })}
                  >
                    <SelectTrigger id="userMode" data-testid="usermode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Agent">Real Estate Agent</SelectItem>
                      <SelectItem value="Private Owner">Private Property Owner</SelectItem>
                      <SelectItem value="Influencer">Social Media Influencer</SelectItem>
                      <SelectItem value="Contractor">Home Services Contractor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                required
                data-testid="password-input"
              />
              {!isLogin && (
                <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
              )}
              {isLogin && (
                <div className="text-right mt-1">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-emerald-600 hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot Password?
                  </button>
                </div>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full bg-emerald-600 hover:bg-emerald-700" 
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Create Account')}
            </Button>

            {/* Google OAuth Button */}
            {isLogin && (
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Or continue with</span>
                </div>
              </div>
            )}

            {isLogin && (
              <Button 
                type="button"
                variant="outline"
                className="w-full flex items-center justify-center gap-3 border-gray-300 hover:bg-gray-50"
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirectUrl = window.location.origin + '/auth/callback';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                data-testid="google-login-btn"
              >
                {/* Official Google Icon */}
                <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span className="font-medium text-gray-700">Continue with Google</span>
              </Button>
            )}

            <div className="text-center pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setFormData({ email: '', password: '', fullName: '', userMode: 'Agent' });
                }}
                className="text-emerald-600 hover:underline text-sm"
              >
                {isLogin ? "Don't have an account? Register" : 'Already have an account? Login'}
              </button>
            </div>
          </form>

          {isLogin && (
            <div className="mt-6 pt-6 border-t text-center">
              <p className="text-sm text-gray-600 mb-3">Browse as Guest</p>
              <Button 
                variant="outline" 
                onClick={() => navigate('/marketplace')}
                className="w-full"
              >
                Continue without Login
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
};

export default Auth;
